import React from 'react';
import { Button } from '../ui/button';
import { Edit2, Send, CheckCircle, XCircle, Lock, History } from 'lucide-react';

export const SalaryTableRow = ({
  record,
  getEmployeeName,
  getEmployeeCode,
  formatCurrency,
  getStatusBadge,
  canEditSalary,
  canApproveSalary,
  isVerifier,
  onEdit,
  onViewDetail,
  onWorkflowAction,
  onViewAudit
}) => {
  return (
    <tr data-testid={`salary-row-${record.id}`}>
      <td>
        <div 
          className="cursor-pointer hover:bg-slate-50 p-1 rounded -m-1"
          onClick={() => onViewDetail(record)}
          data-testid={`view-salary-detail-${record.id}`}
        >
          <p className="font-medium text-slate-900 hover:text-blue-600">{getEmployeeName(record.employee_id)}</p>
          <p className="text-xs text-slate-500">{getEmployeeCode(record.employee_id)}</p>
        </div>
      </td>
      <td>{formatCurrency(record.gross_earnings || record.gross_salary || 0)}</td>
      <td className="text-red-600">{formatCurrency(record.total_deductions || 0)}</td>
      <td className="font-medium text-emerald-600">{formatCurrency(record.net_salary || record.net_pay || 0)}</td>
      <td>
        <span className={getStatusBadge(record.status)}>
          {record.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </span>
      </td>
      <td>
        <div className="flex items-center gap-1">
          {/* Edit - only for draft status and Verifier/HR (NOT Admin) */}
          {record.status === 'draft' && canEditSalary && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onEdit(record)}
              data-testid={`edit-salary-${record.id}`}
            >
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
          
          {/* Submit for verification - Verifier/HR only */}
          {record.status === 'draft' && canEditSalary && (
            <Button
              size="sm"
              variant="ghost"
              className="text-blue-600"
              onClick={() => onWorkflowAction(record.id, 'submit')}
              data-testid={`submit-salary-${record.id}`}
            >
              <Send className="w-4 h-4" />
            </Button>
          )}
          
          {/* Verify - Verifier can verify */}
          {record.status === 'pending_verification' && isVerifier && (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="text-emerald-600"
                onClick={() => onWorkflowAction(record.id, 'verify')}
                data-testid={`verify-salary-${record.id}`}
              >
                <CheckCircle className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-red-600"
                onClick={() => onWorkflowAction(record.id, 'reject')}
                data-testid={`reject-salary-${record.id}`}
              >
                <XCircle className="w-4 h-4" />
              </Button>
            </>
          )}
          
          {/* Approve/Reject - Admin only (on verified records) */}
          {record.status === 'verified' && canApproveSalary && (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="text-emerald-600"
                onClick={() => onWorkflowAction(record.id, 'approve')}
                data-testid={`approve-salary-${record.id}`}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-red-600"
                onClick={() => onWorkflowAction(record.id, 'reject')}
                data-testid={`reject-salary-${record.id}`}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
            </>
          )}
          
          {/* Lock icon for locked/disbursed */}
          {record.is_locked && (
            <Lock className="w-4 h-4 text-slate-400 ml-2" />
          )}
          
          {/* Audit History */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onViewAudit(record.employee_id)}
            data-testid={`view-audit-${record.id}`}
          >
            <History className="w-4 h-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

export default SalaryTableRow;
